import { Link } from "react-router-dom";
import { useState } from "react";
import InfiniteScroll from "../Library/InfiniteScroll";
import { infiniteAlgorithmsQuery } from "./AlgorithmsPageLoader";
import { useInfiniteQuery } from "@tanstack/react-query";

function AlgorithmsPage() {
  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isSuccess,
  } = useInfiniteQuery(
    infiniteAlgorithmsQuery({ searchQuery: searchQuery || "" })
  );

  return (
    <>
      <div className="flex  flex-col-reverse gap-4 mb-8 sm:flex-row sm:justify-between">
        <h1 className="text-3xl font-bold underline">Your Algorithms</h1>
        {/* Search box */}
        <div className="inline-flex">
          <label className="input input-bordered flex items-center gap-2 rounded-none sm:pr-1">
            <input
              type="text"
              className="bg-inherit"
              placeholder="Search"
              onKeyDown={(event) => {
                if (
                  event.key === "Enter" &&
                  event.currentTarget.value !== (searchQuery || "") // in case SQ is still null (first render)
                ) {
                  setSearchQuery(event.currentTarget.value);
                }
              }}
            />
            {/* Search Icon */}
            {/* <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
            fill="currentColor"
            className="w-4 h-4 opacity-70"
          >
            <path
              fillRule="evenodd"
              d="M9.965 11.026a5 5 0 1 1 1.06-1.06l2.755 2.754a.75.75 0 1 1-1.06 1.06l-2.755-2.754ZM10.5 7a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0Z"
              clipRule="evenodd"
            />
          </svg> */}
            {/* Enter kbd hint */}
            <kbd className="kbd whitespace-nowrap shrink-0 gap-[4px]">
              <span className="hidden sm:block">Enter</span>
              <svg
                width="11"
                height="10"
                viewBox="0 0 11 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <line
                  x1="9.09088"
                  x2="9.09088"
                  y2="7.77778"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <line
                  x1="3.63635"
                  y1="6.77778"
                  x2="9.99999"
                  y2="6.77778"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  d="M3.57746e-08 6.66667L4.09091 3.77992L4.09091 9.55342L3.57746e-08 6.66667Z"
                  fill="currentColor"
                />
              </svg>
            </kbd>
            {/* ↵ ↩ */}
            {/* Search Helper + dropdown */}
            <div className="dropdown dropdown-hover dropdown-end hidden sm:block">
              <div
                tabIndex={-1}
                role="button"
                className="btn btn-circle btn-ghost btn-xs text-info"
              >
                <svg
                  tabIndex={0}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  className="w-4 h-4 stroke-current"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  ></path>
                </svg>
              </div>
              <div
                tabIndex={-1}
                className="card compact dropdown-content z-[1] shadow bg-base-100 rounded-box w-80"
              >
                <div tabIndex={0} className="card-body">
                  <h2 className="card-title">Search tips</h2>
                  <ul className="flex flex-wrap flex-col list-disc list-outside ml-3 text-xs text-info space-y-1">
                    <li>
                      If you want to search for an exact phrase, put it in
                      "quotes"
                    </li>
                    <li>
                      Use OR between words to find results that include either
                      word
                    </li>
                    <li>
                      Use a dash (-) before a word to exclude it from results
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </label>
        </div>
      </div>
      {/* List of algorithms */}
      {isLoading ? (
        <div className="h-full flex items-center">
          <span className="loading loading-spinner text-primary w-4 mr-3"></span>
          Searching...
        </div>
      ) : !isSuccess ? (
        "Something went wrong"
      ) : data.pages.length > 0 && data.pages[0].forms.length ? (
        <InfiniteScroll
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          isFetching={isFetchingNextPage}
        >
          <ul className="flex flex-wrap flex-col list-disc list-inside">
            {data.pages
              .flatMap((page) => page.forms)
              .map((algo) => (
                <li key={algo.uuid} className="mb-6">
                  {algo.title}
                  <Link
                    to={`/form/${algo.uuid}`}
                    className="ml-4 btn-link"
                    children="(Demo)"
                  />
                  <Link
                    to={`/algorithm-builder/${algo.uuid}`}
                    className="ml-4 btn-link"
                    children="(Edit)"
                  />{" "}
                  <br />
                </li>
              ))}
          </ul>
        </InfiniteScroll>
      ) : (
        <span>
          {searchQuery ? (
            "No algorithms match your search query. "
          ) : (
            <>
              You don't have any algorithms.{" "}
              <Link to={"/algorithm-builder"} className="btn btn-link">
                Create new algorithms
              </Link>
            </>
          )}
        </span>
      )}
    </>
  );
}

export default AlgorithmsPage;
